<script setup lang="ts" generic="TSubject extends TSchoolSubjectListItem">
import DropdownSelect from '@/core/elements/DropdownSelect.vue';
import EmptyPlaceholder from '@/core/elements/EmptyPlaceholder.vue';
import SchoolSubjectGroupCheckboxList from '@/core/selects/SchoolSubjectGroupCheckboxList.vue';
import type {
  ISelectOption,
  TCompetenceDomainListItem,
  TSchoolSubjectGroupListItem,
  TSchoolSubjectListItem,
} from '@/interfaces';
import { toRefs } from '@vueuse/core';
import { watch } from 'vue';

const props = defineProps<{
  schoolLevelOptions: ISelectOption<number, string>[];
  availableSchoolSubjects: TSubject[];
  availableSchoolSubjectGroups: TSchoolSubjectGroupListItem[];
  availableCompetenceDomains: TCompetenceDomainListItem[];
  noSubjectsMessage: string;
  readOnly?: boolean;
  showEmptyGroups?: boolean;
}>();

const { schoolLevelOptions } = toRefs(props);

const selectedSchoolLevel = defineModel<number | null>('selectedSchoolLevel', { required: true });
const selectedSchoolSubjects = defineModel<TSubject[]>('selectedSchoolSubjects', {
  default: () => [],
});

const normalizeSelectedSchoolLevel = (): void => {
  const options = schoolLevelOptions.value.filter((option) => option.selectable !== false);
  if (!options.length) {
    if (selectedSchoolLevel.value !== null) selectedSchoolLevel.value = null;
    if (selectedSchoolSubjects.value.length) selectedSchoolSubjects.value = [];
    return;
  }

  const allowed = new Set(options.map((o) => o.value));
  const current = selectedSchoolLevel.value;

  if (current == null || !allowed.has(current)) {
    selectedSchoolLevel.value = options[0].value;
    selectedSchoolSubjects.value = [];
  }
};

watch(schoolLevelOptions, () => normalizeSelectedSchoolLevel(), { immediate: true, deep: true });
</script>

<template>
  <div class="grid grid-rows-[auto_1fr] gap-md h-full px-content">
    <!-- @vue-generic {number | null, string} -->
    <DropdownSelect
      v-if="schoolLevelOptions.length"
      v-model="selectedSchoolLevel"
      class="w-min"
      theme="input"
      :options="schoolLevelOptions"
    >
      <template #selected="{ option }">
        {{ option?.label ?? $t('general.schoolLevel') }}
      </template>
      <template #option="{ option }">
        <div
          class="flex min-w-0 flex-1 flex-col text-left font-normal"
          :class="option.selectable === false && 'opacity-[0.45]'"
        >
          <span>{{ option.label }}</span>
          <span v-if="option.details" class="text-footer leading-footer tracking-label text-secondary">
            {{ option.details }}
          </span>
        </div>
      </template>
    </DropdownSelect>

    <SchoolSubjectGroupCheckboxList
      v-if="availableSchoolSubjects.length || (showEmptyGroups && availableSchoolSubjectGroups.length)"
      :key="readOnly ? (selectedSchoolLevel ?? 'empty') : undefined"
      v-model="selectedSchoolSubjects"
      :can-select-groups="!readOnly"
      :read-only="readOnly"
      :show-empty-groups="showEmptyGroups"
      class="h-min gap-md"
      theme="schoolSubjectImport"
      :available-school-subjects="availableSchoolSubjects"
      :available-school-subject-groups="availableSchoolSubjectGroups"
      :available-competence-domains="availableCompetenceDomains"
    />

    <EmptyPlaceholder
      v-else
      :data="availableSchoolSubjects"
      :message="noSubjectsMessage"
      :class="readOnly && 'py-xxxl'"
    />
  </div>
</template>
